import {Async} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentNumber, AgentNumberError, UserAssignedAgentNumber} from './AgentNumber';

export const MAX_AGENT = 8;

export class UnvalidatedAgent {
  public readonly key = 'UnvalidatedAgent';

  constructor(
    public readonly agentNumber: AgentNumber,
    public readonly agentName: StringUnion,
    public readonly percent: number,
    public readonly signed: boolean,
    public readonly allstateAffiliated: boolean
  ) {}
}

export class InvalidAgent {
  public readonly key = 'InvalidAgent';

  constructor(
    public readonly agentNumber: AgentNumber,
    public readonly agentName: StringUnion,
    public readonly percent: number,
    public readonly signed: boolean,
    public readonly allstateAffiliated: boolean
  ) {}
}

type AppProps = {
  agentNumber: AgentNumber;
  agentName: StringUnion;
  percent: number;
  signed: boolean;
  allstateAffiliated: boolean;
};

export class ValidAgent {
  public static createFromApp({agentNumber, agentName, percent, signed, allstateAffiliated}: AppProps) {
    return this.create(agentNumber, agentName, percent, signed, allstateAffiliated);
  }

  public static create(
    agentNumber: AgentNumber,
    agentName: StringUnion,
    percent: number,
    signed: boolean,
    allstateAffiliated: boolean
  ) {
    return isNonEmptyString(agentName)
      ? new ValidAgent(agentNumber, agentName, percent, signed, allstateAffiliated)
      : new InvalidAgent(agentNumber, agentName, percent, signed, allstateAffiliated);
  }

  public readonly key = 'ValidAgent';
  private constructor(
    public readonly agentNumber: AgentNumber,
    public readonly agentName: StringUnion,
    public readonly percent: number,
    public readonly signed: boolean,
    public readonly allstateAffiliated: boolean
  ) {}
}

export type AgentInformation = UnvalidatedAgent | InvalidAgent | ValidAgent;

export type AgentInformationOptions<TReturn> = ToPartialMatchOptions<AgentInformation, 'key', TReturn>;

export const matchAgentInformation = <TReturn>(options: AgentInformationOptions<TReturn>) => (
  app: AgentInformation
): TReturn => {
  switch (app.key) {
    case 'InvalidAgent':
      return options.InvalidAgent ? options.InvalidAgent(app) : options.default(app);
    case 'UnvalidatedAgent':
      return options.UnvalidatedAgent ? options.UnvalidatedAgent(app) : options.default(app);
    case 'ValidAgent':
      return options.ValidAgent ? options.ValidAgent(app) : options.default(app);
  }
};

export const changeAgentNumber = (agentNumber: Async<string>) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    agentNumber,
  });

export const changeUserAgentNumber = (agentNumber: string) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    agentNumber: UserAssignedAgentNumber.create(agentNumber),
  });

export const changeAgentNumberError = (agentNumber: string) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    agentNumber: AgentNumberError.create(agentNumber),
  });

export const changeAgentName = (agentName: string) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    agentName: NonEmptyString.create(agentName),
  });

export const changePercent = (percent: number) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    percent,
  });

export const changeSigned = (signed: boolean) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    signed,
  });

export const changeAllstateAffiliated = (allstateAffiliated: boolean) => (app: AgentInformation) =>
  ValidAgent.createFromApp({
    ...app,
    allstateAffiliated,
  });

export const updateAgentValues = (
  agentNumber: AgentNumber,
  agentName: StringUnion,
  percent: number,
  signed: boolean,
  allstateAffiliated: boolean
) => () => ValidAgent.create(agentNumber, agentName, percent, signed, allstateAffiliated);

export const EmptyAgent = new UnvalidatedAgent(new UserAssignedAgentNumber(''), new EmptyString(), 0, false, false);

export const isValidAgent = (ele: AgentInformation) => {
  return ele.key === 'InvalidAgent' || ele.key === 'UnvalidatedAgent';
};

export const enableSaveButtonAgent = (agentInfo: AgentInformation) => {
  return (
    (agentInfo.agentNumber.key === 'AgentNumberError' || agentInfo.agentNumber.key === 'UserAssignedAgentNumber') &&
    agentInfo.agentNumber.value !== '' &&
    agentInfo.agentNumber.value.length == 5
  );
};
